import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, Target, Calendar, DollarSign, AlertCircle, Loader2 } from 'lucide-react';
import { useCareerProjections } from '@/hooks/useCareerProjections';

interface CareerProjectionsProps {
  occupation_code: string;
  area_code: string;
  className?: string;
}

export default function CareerProjections({
  occupation_code,
  area_code,
  className = ""
}: CareerProjectionsProps) {
  
  const { data: projectionsData, isLoading, error } = useCareerProjections(
    occupation_code,
    area_code
  );

  // Loading state
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Career Projections</CardTitle>
            <Badge variant="outline">Advanced Analytics</Badge>
          </div>
          <CardDescription>
            AI-powered career trajectory analysis for {occupation_code} in {area_code}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Loading skeletons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="text-center p-4 bg-muted/20 rounded-lg">
                <Skeleton className="h-6 w-6 mx-auto mb-2" />
                <Skeleton className="h-8 w-24 mx-auto mb-2" />
                <Skeleton className="h-4 w-32 mx-auto" />
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <Skeleton className="h-6 w-48" />
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Calculating projections...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Career Projections</CardTitle>
            <Badge variant="outline">Advanced Analytics</Badge>
          </div>
          <CardDescription>
            AI-powered career trajectory analysis for {occupation_code} in {area_code}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Unable to Load Projections
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {error.message || 'Failed to load career projections. Please try again later.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!projectionsData) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Career Projections</CardTitle>
            <Badge variant="outline">Pro Analytics</Badge>
          </div>
          <CardDescription>
            AI-powered career trajectory analysis for {occupation_code} in {area_code}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 text-muted-foreground">
            No projection data available for this occupation and area.
          </div>
        </CardContent>
      </Card>
    );
  }

  const { projections, current_salary, growth_rate, volatility_index, insights } = projectionsData;
  const finalProjection = projections[projections.length - 1];
  const projectedGrowth = ((finalProjection.projected_salary - current_salary) / current_salary) * 100;

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Career Projections</CardTitle>
          <Badge variant="outline">Advanced Analytics</Badge>
        </div>
        <CardDescription>
          AI-powered career trajectory analysis for {occupation_code} in {area_code}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Key Projection Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-muted/20 rounded-lg">
            <DollarSign className="h-6 w-6 mx-auto mb-2 text-primary" />
            <div className="text-xl font-bold">${finalProjection.projected_salary.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">{finalProjection.year} Projection</div>
          </div>
          <div className="text-center p-4 bg-muted/20 rounded-lg">
            <TrendingUp className="h-6 w-6 mx-auto mb-2 text-success" />
            <div className="text-xl font-bold">+{projectedGrowth.toFixed(1)}%</div>
            <div className="text-sm text-muted-foreground">5-Year Growth</div>
          </div>
          <div className="text-center p-4 bg-muted/20 rounded-lg">
            <Target className="h-6 w-6 mx-auto mb-2 text-blue-500" />
            <div className="text-xl font-bold">{projections[0].confidence}%</div>
            <div className="text-sm text-muted-foreground">Confidence</div>
          </div>
        </div>

        {/* Projection Timeline */}
        <div className="space-y-4">
          <h4 className="font-medium">5-Year Salary Projections</h4>
          {projections.map((proj) => (
            <div key={proj.year} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{proj.year}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-bold">${proj.projected_salary.toLocaleString()}</span>
                <div className="flex items-center gap-2">
                  <Progress value={proj.confidence} className="w-16" />
                  <span className="text-sm text-muted-foreground">{proj.confidence}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Career Insights */}
        <div className="space-y-3">
          <h4 className="font-medium">Career Insights</h4>
          <div className="space-y-2 text-sm">
            {insights.length > 0 ? (
              insights.map((insight, index) => {
                // Determine color based on insight type
                let colorClass = 'bg-blue-500';
                let description = '';
                
                if (insight.includes('Strong Growth')) {
                  colorClass = 'bg-success';
                  description = `Projected ${growth_rate.toFixed(1)}% annual growth based on historical trends`;
                } else if (insight.includes('Moderate Growth')) {
                  colorClass = 'bg-blue-500';
                  description = `Steady ${growth_rate.toFixed(1)}% growth expected over the next 5 years`;
                } else if (insight.includes('Conservative Growth')) {
                  colorClass = 'bg-amber-500';
                  description = `Modest ${growth_rate.toFixed(1)}% growth projection`;
                } else if (insight.includes('Market Stability')) {
                  colorClass = 'bg-blue-500';
                  description = `Low volatility (${volatility_index.toFixed(2)}) indicates consistent demand`;
                } else if (insight.includes('High Market Volatility')) {
                  colorClass = 'bg-amber-500';
                  description = `Higher volatility (${volatility_index.toFixed(2)}) suggests market uncertainty`;
                } else if (insight.includes('Recession Resilience')) {
                  colorClass = 'bg-success';
                  description = 'Historical data shows resilience during economic downturns';
                } else if (insight.includes('Skill Development')) {
                  colorClass = 'bg-amber-500';
                  description = 'Focus on emerging technologies to maximize growth potential';
                }
                
                return (
                  <div key={index} className="flex items-start gap-2">
                    <div className={`w-2 h-2 ${colorClass} rounded-full mt-2`} />
                    <div>
                      <div className="font-medium">{insight}</div>
                      <div className="text-muted-foreground">{description}</div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-muted-foreground text-sm">
                No specific insights available for this occupation and area.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}